import React from 'react';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
import { useHistory, Link as RouterLink } from 'react-router-dom';
import { IconButton, Grid, Typography, Link, Box } from '@material-ui/core';
import { PlusIcon, MinusIcon } from 'sharedComponents/Icons';
import { statusCodesMap, cancelCodesMap, ICON_MAP } from 'constants/index';
import { ProductPropTypes, getUSFormatDate } from 'utils';
import { PromoInfo, DeliveryDateSelector } from 'sharedComponents';

const ProductDetails = ({
  classes,
  product,
  ordered,
  onChangeQuantity,
  hideQuantityControls,
  inStoreOrder,
  productLink,
  bopsAddress,
  associateStore,
  bopsOrderFlag,
  onChangeDeliveryDate,
  hideDeliveryDateSelector,
  isCheckoutPage,
  confirmationView,
  isReturnDateEnabled,
  isMAOInventoryEnabled,
  userBrand,
  orderId,
}) => {
  const history = useHistory();
  const {
    name,
    brand,
    color,
    size,
    quantity = 0,
    inventory = 0,
    itemskuId,
    trackingNumber,
    isBackorder,
    isDropship,
    expectedShipDate,
    trackingLink,
    isLoading = false,
    status,
    transactionType,
    cancelCode,
    error,
    dept: deptField,
    class: classField,
    cmosOrderNumber,
    posOrderNumber,
    originalOrderNumber,
    returnOrderNumber,
    bopsAvailability,
    subStatus,
    promotions,
    fulfillStore,
    isParenthetical,
    parentheticalCharge,
    isPerishable,
    arriveByDate,
    deliveryDays,
    vendorId,
    narvarTrackingLink,
    currentStatusDate,
    statuses,
  } = product;

  const correctStatus = inStoreOrder ? transactionType : status;

  const isLowInventory = inventory > 0 && inventory < 5;
  const isOutOfStock = quantity > inventory && !isBackorder && !isDropship;

  const minusBtnDisabled = isLoading || quantity === 1;
  const plusBtnDisabled = isLoading || (quantity >= inventory && !isBackorder && !isPerishable);

  const minusBtnClickHandler = () =>
    !minusBtnDisabled &&
    onChangeQuantity &&
    onChangeQuantity('-', {
      ...product,
      quantity: quantity - 1,
    });
  const plusBtnClickHandler = () =>
    !plusBtnDisabled &&
    onChangeQuantity &&
    onChangeQuantity('+', {
      ...product,
      quantity: quantity + 1,
    });

  const isItemUnavailable = isBackorder && !ordered && inventory === 0 && !isDropship;
  const isDisplayExpShipDate = expectedShipDate && !ordered;

  const renderControls = (quantity, minusBtnDisabled, plusBtnDisabled) => (
    <div className={classes.controls}>
      <IconButton disabled={minusBtnDisabled} onClick={minusBtnClickHandler} data-testid="quantity-minus-btn">
        <MinusIcon />
      </IconButton>
      {quantity}
      <IconButton disabled={plusBtnDisabled} onClick={plusBtnClickHandler} data-testid="quantity-plus-btn">
        <PlusIcon />
      </IconButton>
    </div>
  );

  const renderProductDetail = (itemLabel, itemValue) => (
    <Grid container>
      <Grid item xs={3}>
        <Typography variant="body2" color="textSecondary" data-testid={`item-label-${itemLabel}`}>
          {itemLabel}
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Typography variant="body2" className={classes.field} data-testid={`item-value-${itemLabel}`}>
          {itemValue}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderGreyText = (text, isPimSku = false) => (
    <Typography variant="body2" color="textSecondary" data-testid={isPimSku ? 'pim-sku' : 'grey-text'}>
      {text}
    </Typography>
  );

  const renderErrorText = (text) => (
    <Typography variant="body2" color="error" data-testid="error-text">
      {text}
    </Typography>
  );

  const renderFulfillmentText = (text) => (
    <Typography
      variant="body2"
      color="textSecondary"
      data-testid="fulfillment-text"
      className={classes.fulfillmentText}
    >
      Fulfillment location {text}
    </Typography>
  );

  const renderTrackLink = (trackLink, trackNumber, narvarLink = '') => {
    const trackingError = 'Tracking number unavailable. Please contact #111 for assistance';
    const isNarvarLinkAvailable = narvarTrackingLink || narvarLink;
    const correctHref = isNarvarLinkAvailable ? narvarTrackingLink || narvarLink : trackLink;

    const extractTrackNumberFromLink = (trackLink) => {
      const matchResult = trackLink.match(/\d{10,14}$/);
      return matchResult?.length ? matchResult[0] : trackingError;
    };

    const extractNarvarTrackNumberFromLink = (narvarTrackLink) => {
      const soughtSubstring = 'tracking_numbers=';
      const from = narvarTrackLink.indexOf(soughtSubstring) + soughtSubstring.length;
      const to = narvarTrackLink.length;
      const matchResult = narvarTrackLink.slice(from, to);
      return matchResult?.length ? matchResult : trackingError;
    };

    const extractedTrackNumber = isNarvarLinkAvailable
      ? extractNarvarTrackNumberFromLink(narvarTrackingLink || narvarLink)
      : extractTrackNumberFromLink(trackLink);

    const isValidLink = trackNumber || extractedTrackNumber !== trackingError;

    return (
      <Grid container>
        <Grid item xs={3}>
          <Typography variant="body2" color="textSecondary" data-testid="order-track-text">
            Track
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography variant="body2">
            {isValidLink ? (
              <a
                className={classes.trackingLink}
                href={correctHref}
                target="_blank"
                data-testid="order-track-link"
                rel="noopener noreferrer"
              >
                {trackNumber || extractedTrackNumber}
              </a>
            ) : (
              <span className={classes.trackingLink} data-testid="order-track-link">
                {trackingError}
              </span>
            )}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const renderBopsText = () => {
    const defaultLocation = associateStore || localStorage.getItem('lastBopsLocation');
    const storeNumber = Number(bopsAddress?.storeNum || defaultLocation);
    const inStock = _get(bopsAvailability, storeNumber, 0);

    let bopsText = '';
    if (inStock && Number(inStock) > quantity) {
      bopsText = 'Available for pickup today';
    } else if (!inStock) {
      bopsText = 'Available for pickup in 3-7 days';
    }
    return (
      <Typography variant="body2" color="textSecondary" data-testid="bops-text">
        {bopsText}
      </Typography>
    );
  };

  const renderParentheticalShippingText = () => {
    const parentheticalShipChargeText = `Shipping: $${parentheticalCharge}`;
    return (
      <Typography variant="body2" color="textPrimary" data-testid="parenthetical-shipping-text">
        {parentheticalShipChargeText}
      </Typography>
    );
  };

  const renderDeliveryDateSelector = () => {
    const currentDate = arriveByDate && new Date(getUSFormatDate(arriveByDate));

    return (
      <Box className={classes.deliveryDateSelector}>
        <DeliveryDateSelector
          updateDateHandler={(date) => onChangeDeliveryDate(date, product)}
          currentDate={currentDate}
          isCartMode
          isCheckoutPage={isCheckoutPage}
          minDeliveryDateShift={deliveryDays}
          vendorId={vendorId}
        />
        {quantity > 1 &&
          !isCheckoutPage &&
          renderErrorText(
            'Multiple units of the same SKU will be delivered on the same date. If additional delivery dates are needed, please process in a separate transaction',
          )}
      </Box>
    );
  };

  const orderLinkClickHandler = (target) => (evt) => {
    evt.preventDefault();
    history.push(target);
  };

  const renderOrderLink = (text = 'Link', link, isCmosLink = false) => {
    const target = isCmosLink ? `/orders/${link}/cmos` : `/orders/${link}/in-store`;

    return (
      <Grid container>
        <Grid item xs={3}>
          <Typography variant="body2" color="textSecondary" data-testid="order-link">
            {text}
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Link href={target} onClick={orderLinkClickHandler(target)} variant="body2" data-testid="order-link">
            {link}
          </Link>
        </Grid>
      </Grid>
    );
  };

  const shipDate = expectedShipDate === 'Ship Date Unknown' ? expectedShipDate : `Expected to ship ${expectedShipDate}`;

  const productName = productLink ? <RouterLink to={productLink}>{name}</RouterLink> : name;

  const cancelReason =
    status === 'CX' && cancelCode in cancelCodesMap ? `: ${cancelCode} - ${cancelCodesMap[cancelCode]}` : '';

  const determineDisplayStatus = () => {
    if (transactionType === 'VOID') {
      return `${statusCodesMap[transactionType].type} (${statusCodesMap[transactionType].text})`;
    }
    if (transactionType !== 'SEND' && inStoreOrder) {
      return transactionType;
    }
    if (subStatus) {
      return subStatus.toLowerCase() === 'item returned' ? 'Package Picked Up' : subStatus;
    }
    /**
     * InStore orders don't have statuses associated with the product details.
     */
    if (!status) {
      return transactionType;
    }
    if (statusCodesMap[status].type === 'Returned' && isReturnDateEnabled) {
      return `${statusCodesMap[status].type}: ${currentStatusDate} (${statusCodesMap[status].text})`;
    }
    return `${statusCodesMap[status].type} (${statusCodesMap[status].text})${cancelReason}`;
  };

  const renderStatusField = () => (
    <Grid container>
      <Grid item xs={3}>
        <Typography variant="body2" color="textSecondary" data-testid="order-status-text">
          Status
        </Typography>
      </Grid>
      <Grid item xs={9}>
        <Typography variant="body2" className={classes.field} data-testid="order-status-number">
          {determineDisplayStatus()}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderStatusesField = () => {
    const productStatusContainer = [];
    statuses.forEach((productStatus, i) => {
      const color =
        ICON_MAP[statusCodesMap[productStatus?.status || productStatus?.transactionType]?.type]?.color || '#000000';
      let status = '';
      if (productStatus?.status) {
        status = productStatus?.subStatus
          ? productStatus?.subStatus
          : `${statusCodesMap[productStatus?.status]?.type} (${statusCodesMap[productStatus.status]?.text})`;
      } else if (productStatus?.transactionType === 'VOID') {
        status = `${statusCodesMap[productStatus?.transactionType]?.type} (${
          statusCodesMap[productStatus?.transactionType]?.text
        })`;
      } else {
        status = productStatus?.transactionType;
      }
      productStatusContainer.push(
        <>
          <Grid container key={`qty_${i}`} className={classes.statusField}>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary" data-testid="order-status-text">
                Qty
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body2" className={classes.field} data-testid="order-status-number">
                {productStatus.quantity}
              </Typography>
            </Grid>
          </Grid>
          {productStatus?.fulfillmentLocation && (
            <div key={`fulfillment_${i}`}>
              <Typography variant="body2" color="textSecondary" data-testid="order-status-text">
                Fulfillment Location {productStatus?.fulfillmentLocation}
              </Typography>
            </div>
          )}
          <Grid container key={`status_${i}`}>
            <Grid item xs={3}>
              <Typography variant="body2" color="textSecondary" data-testid="order-status-text">
                Status
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography
                variant="body2"
                className={classes.field}
                style={{ color }}
                data-testid="order-status-number"
                onClick={() => {
                  history.push('/additional-order-details', {
                    userBrand,
                    orderId,
                    selectedStatus: productStatus,
                    productDetailData: product,
                  });
                }}
              >
                {status}
              </Typography>
            </Grid>
          </Grid>
          {productStatus?.narvarTrackingLink &&
            renderTrackLink(
              productStatus?.trackingLink,
              productStatus?.carrierTrackingNumber,
              productStatus?.narvarTrackingLink,
            )}
        </>,
      );
    });
    return productStatusContainer;
  };

  const renderCMOSOrderNumber = (cmosOrderNumber) => {
    if (!cmosOrderNumber) return null;

    return renderProductDetail('CMOS', cmosOrderNumber);
  };

  const renderShipText = (shipDate) =>
    shipDate === 'Ship Date Unknown' ? renderErrorText(shipDate) : renderGreyText(shipDate);

  const hideDeliveryDateWarningForPerishable = isPerishable;

  return (
    <div className={classes.productDetails}>
      {error && <Typography>{error}</Typography>}
      {brand && <Typography variant="h5">{brand}</Typography>}
      {name && <Typography className={confirmationView ? classes.prodNameBlack : ''}>{productName}</Typography>}
      {itemskuId && renderGreyText(`PIM SKU ${itemskuId}`, true)}
      {deptField && renderGreyText(`Department ${deptField}`)}
      {classField && renderGreyText(`Class ${classField}`)}
      {!isLowInventory && !isOutOfStock && !ordered && !isBackorder && !isDropship && renderGreyText('In Stock')}
      {!!bopsOrderFlag && renderBopsText()}
      {isLowInventory && !ordered && renderGreyText(`Only ${inventory} Left`)}
      {isOutOfStock && !ordered && renderErrorText(!inventory ? 'Out of Stock' : `Only ${inventory} available`)}
      {isItemUnavailable && renderErrorText('Item unavailable')}
      {isDropship && renderErrorText('Ships from vendor')}
      {isDisplayExpShipDate && !hideDeliveryDateWarningForPerishable && renderShipText(shipDate)}
      {color && color !== 'NO COLOR' && renderProductDetail('Color', color)}
      {size && size !== 'NO SIZE' && renderProductDetail('Size', size)}
      <PromoInfo promotions={promotions} />
      {isParenthetical && !hideQuantityControls && renderParentheticalShippingText()}
      {hideQuantityControls
        ? renderProductDetail(isMAOInventoryEnabled ? 'Total Qty' : 'Qty', quantity)
        : renderControls(quantity, minusBtnDisabled, plusBtnDisabled)}

      {!hideDeliveryDateSelector && isPerishable && renderDeliveryDateSelector()}

      {isParenthetical && hideQuantityControls && renderParentheticalShippingText()}
      <div className={classes.statusField}>
        {fulfillStore && renderFulfillmentText(fulfillStore)}
        {posOrderNumber && renderOrderLink('POS', posOrderNumber)}
        {renderCMOSOrderNumber(cmosOrderNumber)}
        {originalOrderNumber && renderOrderLink('Orig', originalOrderNumber)}
        {returnOrderNumber?.map((order) => renderOrderLink('Return', order))}
        {(correctStatus || subStatus) && renderStatusField()}
        {statuses && renderStatusesField()}
      </div>
      {trackingLink && !isMAOInventoryEnabled && renderTrackLink(trackingLink, trackingNumber)}
    </div>
  );
};

ProductDetails.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  product: ProductPropTypes,
  ordered: PropTypes.bool,
  hideQuantityControls: PropTypes.bool,
  onChangeQuantity: PropTypes.func,
  inStoreOrder: PropTypes.bool,
  productLink: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.shape({}),
  }),
  onChangeDeliveryDate: PropTypes.func,
  isCheckoutPage: PropTypes.bool,
  hideDeliveryDateSelector: PropTypes.bool,
  isReturnDateEnabled: PropTypes.bool,
};

ProductDetails.defaultProps = {
  classes: {},
  product: {},
  hideQuantityControls: false,
  ordered: false,
  inStoreOrder: false,
  onChangeDeliveryDate: () => {},
  isCheckoutPage: true,
  isReturnDateEnabled: false,
};

export default ProductDetails;
